import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Fab,
  MenuItem,
  Tooltip,
  List,
  ListItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Category as CategoryIcon,
  Timeline as TimelineIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';

const categoryColors = {
  ceremony: '#FF6B6B',
  reception: '#4ECDC4',
  photo: '#45B7D1',
  setup: '#96CEB4',
  other: '#B0BEC5', // neutral gray, no yellow
};

const _categoryIcons = {
  ceremony: '💒',
  reception: '🍾',
  photo: '📸',
  setup: '🔧',
  other: '📝',
};

function Timeline() {
  const styles = useThemeStyles();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  const [eventForm, setEventForm] = useState({
    title: '',
    time: { hour: 12, minute: 0, period: 'PM' },
    duration: 60,
    description: '',
    location: '',
    category: 'other',
    day: 1,
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/timeline');
      // Sort by order field, fallback to creation time
      const sortedEvents = response.data.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return new Date(a.createdAt) - new Date(b.createdAt);
      });
      setEvents(sortedEvents);
    } catch (error) {
      setError('Failed to load timeline events');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = () => {
    setEditingEvent(null);
    setEventForm({
      title: '',
      time: { hour: 12, minute: 0, period: 'PM' },
      duration: 60,
      description: '',
      location: '',
      category: 'other',
      day: 1,
    });
    setOpenDialog(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title,
      time: event.time,
      duration: event.duration || 60,
      description: event.description || '',
      location: event.location || '',
      category: event.category || 'other',
      day: event.day || 1,
    });
    setOpenDialog(true);
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await api.delete(`/api/timeline/${eventId}`);
      setEvents(events.filter(e => e._id !== eventId));
    } catch (error) {
      setError('Failed to delete event');
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingEvent) {
        const response = await api.put(`/api/timeline/${editingEvent._id}`, eventForm);
        setEvents(events.map(e => e._id === editingEvent._id ? response.data : e));
      } else {
        // Add new event at the end of the list
        const newEvent = {
          ...eventForm,
          order: events.length
        };
        const response = await api.post('/api/timeline', newEvent);
        setEvents([...events, response.data]);
      }
      setOpenDialog(false);
    } catch (error) {
      setError('Failed to save event');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(events);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order locally
    const reorderedEvents = items.map((item, index) => ({
      ...item,
      order: index
    }));
    setEvents(reorderedEvents);

    // Update order on server
    try {
      const response = await api.put('/api/timeline/reorder', {
        events: reorderedEvents.map(e => ({ _id: e._id, order: e.order }))
      });
      // Refresh events from server to ensure consistency
      if (response.data && Array.isArray(response.data)) {
        setEvents(response.data);
      }
    } catch (error) {
      setError('Failed to update event order');
      fetchEvents(); // Revert on error
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const { hour, minute, period } = time;
    return `${hour}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  const getDayLabel = (day) => {
    switch (day) {
      case 1: return 'Wedding Day';
      case 2: return 'Reception Day';
      case 3: return 'Day 3';
      case 4: return 'Day 4';
      case 5: return 'Day 5';
      default: return `Day ${day}`;
    }
  };

  if (loading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container.page}>
      <Box sx={{
        ...styles.patterns.pageHeader,
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 0 },
      }}>
        <Box sx={{ ...styles.layout.flexRow, gap: 2, alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
          <TimelineIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, textAlign: { xs: 'center', sm: 'left' } }}>
            Wedding Timeline
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddEvent}
          size="medium"
          sx={{ alignSelf: { xs: 'stretch', sm: 'inherit' }, minWidth: { xs: 'auto', sm: undefined } }}
        >
          Add Event
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {events.length === 0 ? (
        <Card sx={styles.card.default}>
          <CardContent>
            <Box sx={styles.patterns.emptyState}>
              <EventIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No timeline events yet
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Start planning your wedding timeline by adding events
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddEvent}
              >
                Add First Event
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {/* Timeline Header */}
          <Box sx={styles.layout.flexRow} gap={2} mb={2} alignItems="center">
            <Typography variant="h6" color="primary">
              Timeline Events ({events.length})
            </Typography>
            <Chip 
              label="Drag to reorder"
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>

          {/* Draggable Timeline List */}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="timeline">
              {(provided) => (
                <List
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{ p: 0, minHeight: 100 }}
                >
                  {events.map((event, index) => (
                    <Draggable key={event._id} draggableId={event._id?.toString() || event._id} index={index}>
                      {(provided, snapshot) => (
                        <ListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          sx={{
                            p: 0,
                            mb: 2,
                            opacity: snapshot.isDragging ? 0.7 : 1,
                            background: 'none',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            boxShadow: 'none',
                            transition: 'box-shadow 0.2s',
                            '&:hover': { boxShadow: 2 },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', p: 2 }}>
                            <Box
                              {...provided.dragHandleProps}
                              sx={{
                                cursor: 'grab',
                                display: 'flex',
                                alignItems: 'center',
                                color: 'text.secondary',
                                mr: 2,
                                '&:hover': { color: 'primary.main' },
                              }}
                            >
                              <DragIcon />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                                <Chip
                                  icon={<TimeIcon />}
                                  label={formatTime(event.time)}
                                  color="primary"
                                  variant="outlined"
                                  size="small"
                                  sx={{ fontWeight: 500 }}
                                />
                                <Chip
                                  icon={<CategoryIcon />}
                                  label={event.category}
                                  sx={{
                                    backgroundColor: categoryColors[event.category] || categoryColors.other,
                                    color: '#263238',
                                    fontWeight: 500,
                                  }}
                                  size="small"
                                />
                                <Chip
                                  label={getDayLabel(event.day)}
                                  variant="outlined"
                                  size="small"
                                  color="secondary"
                                  sx={{ fontWeight: 500 }}
                                />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {event.title}
                              </Typography>
                              {event.description && (
                                <Typography variant="body2" color="text.secondary">
                                  {event.description}
                                </Typography>
                              )}
                              {event.location && (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {event.location}
                                  </Typography>
                                </Box>
                              )}
                              {event.duration && (
                                <Typography variant="body2" color="text.secondary">
                                  Duration: {event.duration} minutes
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', ml: 2 }}>
                              <Tooltip title="Edit event">
                                <IconButton onClick={() => handleEditEvent(event)} size="small">
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete event">
                                <IconButton onClick={() => handleDeleteEvent(event._id)} size="small" color="error">
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      )}

      {/* Floating Action Button for Mobile */}
      <Fab
        color="primary"
        aria-label="add event"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', md: 'none' },
        }}
        onClick={handleAddEvent}
      >
        <AddIcon />
      </Fab>

      {/* Event Form Dialog */}
      <TimelineEventDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        eventForm={eventForm}
        setEventForm={setEventForm}
        onSubmit={handleSubmit}
        editingEvent={editingEvent}
      />


    </Box>
  );
}

// Timeline Event Dialog Component
function TimelineEventDialog({ open, onClose, eventForm, setEventForm, onSubmit, editingEvent }) {
  const styles = useThemeStyles();

  const handleTimeChange = (field, value) => {
    setEventForm(prev => ({
      ...prev,
      time: {
        ...prev.time,
        [field]: value
      }
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingEvent ? 'Edit Event' : 'Add New Event'}
      </DialogTitle>
      <DialogContent>
        <Box sx={styles.patterns.formContainer} mt={2}>
          <TextField
            fullWidth
            label="Event Title"
            value={eventForm.title}
            onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
            required
            sx={{ mb: 2 }}
          />
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Hour"
                type="number"
                value={eventForm.time.hour}
                onChange={(e) => handleTimeChange('hour', parseInt(e.target.value))}
                inputProps={{ min: 1, max: 12 }}
                required
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Minute"
                type="number"
                value={eventForm.time.minute}
                onChange={(e) => handleTimeChange('minute', parseInt(e.target.value))}
                inputProps={{ min: 0, max: 59 }}
                required
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                select
                label="Period"
                value={eventForm.time.period}
                onChange={(e) => handleTimeChange('period', e.target.value)}
                required
              >
                <MenuItem value="AM">AM</MenuItem>
                <MenuItem value="PM">PM</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                select
                label="Day"
                value={eventForm.day}
                onChange={(e) => setEventForm({ ...eventForm, day: parseInt(e.target.value) })}
                required
              >
                <MenuItem value={1}>Day 1</MenuItem>
                <MenuItem value={2}>Day 2</MenuItem>
                <MenuItem value={3}>Day 3</MenuItem>
                <MenuItem value={4}>Day 4</MenuItem>
                <MenuItem value={5}>Day 5</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Duration (minutes)"
                type="number"
                value={eventForm.duration}
                onChange={(e) => setEventForm({ ...eventForm, duration: parseInt(e.target.value) })}
                inputProps={{ min: 15, step: 15 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={eventForm.category}
                onChange={(e) => setEventForm({ ...eventForm, category: e.target.value })}
              >
                <MenuItem value="ceremony">Ceremony</MenuItem>
                <MenuItem value="reception">Reception</MenuItem>
                <MenuItem value="photo">Photo</MenuItem>
                <MenuItem value="setup">Setup</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          
          <TextField
            fullWidth
            label="Location"
            value={eventForm.location}
            onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            value={eventForm.description}
            onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={onSubmit} 
          variant="contained"
          disabled={!eventForm.title || !eventForm.time.hour}
        >
          {editingEvent ? 'Update' : 'Add'} Event
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Timeline; 